import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    useOrgEmployeeStore,
    selectOrgEmployee,
    selectOrgEmployeeLoading,
    selectOrgEmployeeError,
} from '@store/orgEmployee';

/**
 * Custom hook that provides the employeeId and orgEmployeeId from org-employee
 * data. Centralizes the fetching of org-employee and extraction of IDs.
 *
 * This is a cross-cutting hook (Auth0 + orgEmployee store) and therefore lives
 * in `@shared/hooks` rather than inside a feature.
 *
 * @returns employeeId, orgEmployeeId, and loading/error states
 */
export function useEmployeeId(): {
    employeeId: string | null;
    orgEmployeeId: string | null;
    isLoading: boolean;
    error: string | null;
} {
    const { getAccessTokenSilently } = useAuth0();

    // Get orgEmployee data from the centralized store
    const orgEmployee = useOrgEmployeeStore(selectOrgEmployee);
    const orgEmployeeLoading = useOrgEmployeeStore(selectOrgEmployeeLoading);
    const orgEmployeeError = useOrgEmployeeStore(selectOrgEmployeeError);
    const fetchOrgEmployee = useOrgEmployeeStore(
        (state): ((token: string) => Promise<void>) => state.fetchOrgEmployee,
    );

    // Extract employeeId and orgEmployeeId from orgEmployee
    const employeeId = orgEmployee?.employees?.[0]?.id ?? null;
    const orgEmployeeId = orgEmployee?.id ?? null;

    // Fetch orgEmployee on mount if not already available
    useEffect((): void => {
        if (!orgEmployee) {
            void (async (): Promise<void> => {
                try {
                    const token = await getAccessTokenSilently();
                    void fetchOrgEmployee(token);
                } catch {
                    // Authentication error - will be handled by the store
                }
            })();
        }
    }, [fetchOrgEmployee, orgEmployee, getAccessTokenSilently]);

    return {
        employeeId,
        orgEmployeeId,
        isLoading: orgEmployeeLoading,
        error: orgEmployeeError,
    };
}
